/**
 * מסך "ההפניות שלי"  —  /my-leads
 *
 * הרכזת רואה כאן את ההפניות שהיא פתחה, ומה קרה עם כל אחת. עד היום היא הזינה
 * הפנייה, קיבלה "נוצר בהצלחה", וההפנייה נעלמה - לא היה שום מסך שאפשר לשאול בו
 * "את מי הזנתי?" או "האם כבר הזנתי את האיש הזה?".
 *
 * המחוז לא נשלח לשרת: הוא נגזר מהמשתמשת המחוברת, כמו ביצירת הפנייה. מחוז
 * שאפשר לשלוח הוא מחוז שאפשר לשנות.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import toast from 'react-hot-toast';
import { Lead, LeadSearchField, listMyLeads } from '../services/leadService';
import { formatDate } from '../utils/dateUtils';
import { getApiError } from '../utils/apiError';

// The server searched all of these all along; what was missing was the ability
// to say which one. It matters because an obligation number is nine digits and
// so is a national ID: a number pasted into a search that looks in both comes
// back with a row and no way to tell which column answered (Shachar, 10/09:
// "some search, by name, by obligation").
//
// "הכל" stays the default, so the screen opens behaving exactly as before.
const SEARCH_FIELDS: { value: LeadSearchField; label: string }[] = [
  { value: 'all', label: 'הכל' },
  { value: 'name', label: 'שם' },
  { value: 'id_number', label: 'תעודת זהות' },
  { value: 'phone', label: 'טלפון' },
  { value: 'obligation_number', label: 'התחייבות' },
];

// The three controls read as one set, so they get one width rather than three
// widths that happen to fit their own contents.
const CONTROL = { flex: '1 1 220px', minWidth: 200, maxWidth: 300 } as const;

// 15, which is also the server's own default (leads.py). Shachar picked the
// number watching the South's 662 rows arrive: "pages of up to 15, say".
const ROWS_PER_PAGE = 15;

const MyLeadsPage: React.FC = () => {
  const [rows, setRows] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<LeadSearchField>('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listMyLeads({
        search: search.trim() || undefined,
        searchField,
        skip: page * ROWS_PER_PAGE,
        limit: ROWS_PER_PAGE,
      });
      setRows(result.items);
      setTotal(result.total);
    } catch (err) {
      // The server's own reason, translated - not a generic message. The two
      // answers she can actually reach here are "no district configured" and
      // "no permission to view referrals", and both are settings on her user
      // rather than faults: saying so is the difference between a five minute
      // fix and a phone call that starts with "it does not work".
      toast.error(getApiError(err, 'שגיאה בטעינת ההפניות'));
    } finally {
      setLoading(false);
    }
  }, [page, search, searchField]);

  // Typing straight into a request per keystroke would send one call per letter
  // of a nine digit ID.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [search, searchField]);

  return (
    <Box dir="rtl">
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
        ההפניות שלי
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        ההפניות שנפתחו במחוז שלך, החדשות ראשונות
      </Typography>

      {/* The picker first, so in RTL it reads right to left the way she works:
          pick what you are looking for, then type it in the box beside it. */}
      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2, '& > *': CONTROL }}>
        {/* size="small" because the theme sets it on MuiTextField and a Select
            inside a bare FormControl does not inherit it - the outer boxes
            matched at 300x56 while the bordered inputs were 56 and 40. */}
        <FormControl size="small">
          <InputLabel id="lead-search-field-label">חיפוש לפי</InputLabel>
          <Select
            labelId="lead-search-field-label"
            value={searchField}
            label="חיפוש לפי"
            onChange={(e) => setSearchField(e.target.value as LeadSearchField)}
          >
            {SEARCH_FIELDS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={searchField === 'all'
            ? 'חיפוש'
            : SEARCH_FIELDS.find((f) => f.value === searchField)?.label}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {search ? 'לא נמצאו הפניות התואמות לחיפוש' : 'עדיין לא נפתחו הפניות'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">תאריך פתיחה</TableCell>
                <TableCell align="right">שם</TableCell>
                <TableCell align="right">תעודת זהות</TableCell>
                <TableCell align="right">טלפון</TableCell>
                <TableCell align="right">התחייבות</TableCell>
                <TableCell align="right">קובץ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell align="right">{formatDate(lead.created_at)}</TableCell>
                  <TableCell align="right">{lead.first_name} {lead.last_name}</TableCell>
                  <TableCell align="right">{lead.id_number}</TableCell>
                  <TableCell align="right" sx={{ direction: 'ltr', textAlign: 'right' }}>
                    {lead.phone}
                  </TableCell>
                  <TableCell align="right">{lead.obligation_number}</TableCell>
                  <TableCell align="right">
                    {lead.referral_file_id ? <DescriptionIcon fontSize="small" color="action" /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default MyLeadsPage;
